import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'src/reducers';
import { AppContext } from '@prisjakt/ui-app-context';

import { PriceListFilterContext } from 'src/areas/product/price-list-filter-context';
import { useEffectOnMount } from 'src/hooks/useEffectOnMount';
import RouteContext from 'src/routes/context';
import { IFilterState, PriceDisplayEnum } from 'src/areas/product/types';
import { priceShippingDisplay } from 'src/areas/product/actions/product';
import { getAllFilters } from '../utils/filters-config';
import {
  trackPriceListSortUpdate,
  trackPriceListFilterUpdate,
} from '../../../tracking';
import {
  getFiltersFromUserSettings,
  saveFiltersAsUserSettings,
  defaultHiddenListOptions,
} from '../utils';
import { FilterEnum, FilterType } from '../types';
import { Axis } from '@prisjakt/ui';
import { Hideable } from '@prisjakt/ui/hideable';
import FilterOption from './filter-option';
import { FilterPanel, FilterPanelWrapper } from '../styles';
import ProductFilters from '../../../product-filters';
import { ProductConditionSummary, VariantGroup } from 'src/types';

const FilterOptionsPanel: React.FC<IFilterOptionsPanelProps> = props => {
  const { visible, variantGroups, conditions, categoryId } = props;
  const { t, market } = useContext(AppContext);
  const { dispatch: routeDispatch } = useContext(RouteContext);
  const dispatch = useDispatch();
  const {
    filters,
    setFilters,
    selectedVariant,
    selectedCondition,
  } = useContext(PriceListFilterContext);
  const [hiddenListOptions] = useState<string[]>(
    defaultHiddenListOptions(market)
  );
  const priceDisplay = useSelector(
    (state: AppState) => state.areas.product.priceDisplay
  );
  const filtersConfig = getAllFilters(t, filters, hiddenListOptions);

  const reload = (selectedFilters: IFilterState) => {
    setFilters(selectedFilters);
    routeDispatch({
      type: 'PRICE_FILTER',
      options: {
        ...selectedFilters,
      },
      // selectedVariant.id === 0 corresponds to "all", which should not be fetched by id
      variants:
        selectedVariant && selectedVariant.id !== 0
          ? [selectedVariant.id]
          : null,
      condition: selectedCondition,
    });
  };

  const onProductTypeFilterChanged = (name: string, val: boolean) => {
    const selectedFilters = {
      ...filters,
      [name]: val,
    };
    saveFiltersAsUserSettings(selectedFilters);
    trackPriceListFilterUpdate(name, val);
    reload(selectedFilters);
  };

  const onPricesIncShippingOptionChanged = (name: string, val: boolean) => {
    const selectedFilters = {
      ...filters,
      [name]: val,
    };
    const selectedPriceDisplay =
      priceDisplay === PriceDisplayEnum.PRICE
        ? PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
        : PriceDisplayEnum.PRICE;
    saveFiltersAsUserSettings(selectedFilters);
    trackPriceListSortUpdate(selectedPriceDisplay);
    dispatch(priceShippingDisplay(selectedPriceDisplay));
    reload(selectedFilters);
  };

  const setPricesIncShippingOptionFromUserSettings = (
    storedFilters: IFilterState
  ) => {
    const isPricesIncShipping = storedFilters[FilterEnum.PRICES_INC_SHIPPING];
    if (isPricesIncShipping !== null) {
      const selectedPriceDisplay = isPricesIncShipping
        ? PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
        : PriceDisplayEnum.PRICE;
      if (selectedPriceDisplay !== priceDisplay) {
        dispatch(priceShippingDisplay(selectedPriceDisplay));
      }
    }
  };

  const setProductTypeFiltersFromUserSettings = (
    storedFilters: IFilterState
  ) => {
    if (JSON.stringify(storedFilters) !== JSON.stringify(filters)) {
      reload(storedFilters);
    }
  };

  useEffectOnMount(() => {
    const storedFilters = getFiltersFromUserSettings(filters);
    setPricesIncShippingOptionFromUserSettings(storedFilters);
    setProductTypeFiltersFromUserSettings(storedFilters);
  });

  if (!Array.isArray(filtersConfig)) {
    return null;
  }

  return (
    <Hideable visible={visible} collapse={Axis.Y}>
      <FilterPanelWrapper>
        <ProductFilters
          variantGroups={variantGroups}
          conditions={conditions}
          categoryId={categoryId}
        />
        <FilterPanel>
          {filtersConfig.map(priceFilter => {
            return (
              !priceFilter.hide && (
                <FilterOption
                  key={priceFilter.name}
                  name={priceFilter.name}
                  defaultValue={priceFilter.checked}
                  onFilterChangedHandler={
                    priceFilter.type === FilterType.PRODUCT_PRICE_TYPE
                      ? onProductTypeFilterChanged
                      : onPricesIncShippingOptionChanged
                  }
                  label={priceFilter.label}
                  caption={priceFilter.caption}
                />
              )
            );
          })}
        </FilterPanel>
      </FilterPanelWrapper>
    </Hideable>
  );
};

interface IFilterOptionsPanelProps {
  visible: boolean;
  variantGroups?: VariantGroup[] | null;
  conditions: ProductConditionSummary[];
  categoryId: string | undefined;
}

export default FilterOptionsPanel;
